import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { StatusDotPill } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { useNavigate } from 'react-router-dom';
import type { SpareRequisition } from './useData';
import { duplicateSpareRequisition } from '../../../services/mutations';

export const moduleConfig = {
  module: {
    title: 'Spare Requisition',
    permissionModuleKey: 'spare_requisitions',
    route: {
      list: '/spares/requisitions',
      new: '/spares/requisitions/new',
      detail: (id: number) => `/spares/requisitions/${id}`,
      edit: (id: number) => `/spares/requisitions/${id}/edit`},
    api: {
      list: '/spare-requisitions',
      detail: (id: number) => `/spare-requisitions/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case or vessel…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'ref', 'saveStatus', 'caseRef', 'categories', 'reqDate']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SpareRequisition) => item.vessel?.vesselName || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SpareRequisition) => item.ref || '',
        options: []},
      {
        key: 'saveStatus',
        label: 'Status',
        type: 'select' as const,
        placeholder: 'All statuses',
        getValue: (item: SpareRequisition) => item.saveStatus || 'SAVED',
        options: [
          { value: 'DRAFT', label: 'Draft' },
          { value: 'SAVED', label: 'Saved' },
        ]},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SpareRequisition) => item.spareCase?.ref || '',
        options: []},
      {
        key: 'categories',
        label: 'Categories',
        type: 'select' as const,
        placeholder: 'All categories',
        getValue: (item: SpareRequisition) =>
          (item.categories ?? [])
            .map((c) => c.name?.trim() || c.abrv?.trim() || '')
            .filter(Boolean)
            .join(', '),
        options: []},
      {
        key: 'reqDate',
        label: 'Date',
        type: 'date' as const,
        placeholder: 'All dates',
        getValue: (item: SpareRequisition) => item.reqDate || '',
        options: []},
    ],
    applyFilter: (
      items: SpareRequisition[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      const isEmptySentinel = (f: string) => f === '(Empty)';
      return items.filter((item) => {
        const vesselFilters = asArray(filters.vessel);
        if (vesselFilters.length) {
          const vesselName = (item.vessel?.vesselName || '').trim().toLowerCase();
          const hasVessel = Boolean(item.vesselId) || vesselName.length > 0;
          if (
            !vesselFilters.some((f) =>
              isEmptySentinel(f) ? !hasVessel : vesselName.includes(String(f).toLowerCase()),
            )
          ) {
            return false;
          }
        }

        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = (item.ref || '').trim();
          if (
            !refFilters.some((f) =>
              isEmptySentinel(f) ? v === '' : v.toLowerCase().includes(String(f).toLowerCase()),
            )
          ) {
            return false;
          }
        }

        const statusFilters = asArray(filters.saveStatus);
        if (statusFilters.length) {
          const normalizeStatus = (s: string) => {
            const v = s.trim();
            const l = v.toLowerCase();
            if (v === 'DRAFT' || l === 'draft') return 'DRAFT';
            if (v === 'SAVED' || l === 'saved') return 'SAVED';
            return v;
          };
          const st = item.saveStatus || 'SAVED';
          if (!statusFilters.some((f) => normalizeStatus(f) === st)) return false;
        }

        const caseFilters = asArray(filters.caseRef);
        if (caseFilters.length) {
          const c = (item.spareCase?.ref || '').trim().toLowerCase();
          if (
            !caseFilters.some((f) =>
              isEmptySentinel(f) ? !item.caseId || c === '' : c.includes(String(f).toLowerCase()),
            )
          ) {
            return false;
          }
        }

        const categoryFilters = asArray(filters.categories);
        if (categoryFilters.length) {
          const categoryLabels = (item.categories ?? [])
            .map((c) => c.name?.trim() || c.abrv?.trim() || '')
            .filter(Boolean)
            .map((v) => v.toLowerCase());
          const noCategories = (item.categoryIds ?? []).length === 0;
          if (
            !categoryFilters.some((f) =>
              isEmptySentinel(f) ? noCategories : categoryLabels.includes(String(f).toLowerCase()),
            )
          ) {
            return false;
          }
        }

        const reqDateFilter = dateRanges?.reqDate;
        if (reqDateFilter?.start || reqDateFilter?.end) {
          const itemDateRaw = item.reqDate ? new Date(item.reqDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (reqDateFilter.start && itemDateRaw < reqDateFilter.start) return false;
          if (reqDateFilter.end && itemDateRaw > reqDateFilter.end) return false;
        } else {
          const reqDateValues = asArray(filters.reqDate);
          if (reqDateValues.length) {
            const itemDate = toDateOnly(item.reqDate);
            if (!reqDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }
        return true;
      });
    },
    applySearch: (items: SpareRequisition[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const categoryBits = (item.categories ?? [])
          .map((c) => [c.name, c.abrv].filter(Boolean).join(' '))
          .join(' ');
        const parts = [
          item.ref,
          item.notes,
          item.spareCase?.ref,
          item.vessel?.vesselName,
          String(item.id),
          categoryBits,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New requisition',
        showTitle: false,
        variant: 'primary' as const,
        href: '/spares/requisitions/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SpareRequisition) => item.id,
  },
  emptyStates: {
    noData: 'Create a requisition to get started.',
    noSearchResults: 'No requisitions match your search.',
    noFilterResults: 'No requisitions match the filters.',
  },
  delete: {
    title: 'Delete requisition',
    message:
      'Permanently delete this requisition and its lines. This cannot be undone. Not allowed while it is on a spare case (remove it from the case first) or while a supplier enquiry, quotation, or purchase order is linked.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete requisition.',
    handler: async (id: number) => {
      const { deleteSpareRequisition } = await import('../../../services/mutations');
      await deleteSpareRequisition(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  /** Used by MainLayout for page titles on custom edit route */
  form: {
    new: { title: 'New requisition', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit requisition', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const ReqCategoriesCell: React.FC<{ row: SpareRequisition }> = ({ row }) => {
  const { colors } = useTheme();
  const labels =
    row.categories && row.categories.length > 0
      ? row.categories
          .map((c) => c.name?.trim() || c.abrv?.trim() || `#${c.id}`)
          .filter(Boolean)
      : row.categoryIds && row.categoryIds.length > 0
        ? row.categoryIds.map((id) => `#${id}`)
        : [];
  const text = labels.join(', ');
  return (
    <div className="max-w-[16rem] py-0.5" title={text || undefined}>
      <span className={cn('text-sm line-clamp-2', colors.text)}>{labels.length ? text : '—'}</span>
    </div>
  );
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `spare-cases/config.tsx`. */
function spareRequisitionCaseIdForView(row: SpareRequisition | undefined): number | null {
  const id = row?.caseId ?? row?.spareCase?.id ?? 0;
  return id > 0 ? id : null;
}

const CaseColumnCell: React.FC<{ row: SpareRequisition }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = spareRequisitionCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.spareCase?.ref?.trim() || `#${caseId}`;
  const href = `/spares/spare-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

/** Same pill chrome as spare-cases `CaseStatusCell`; dot color matches Draft vs Saved (like case status semantics). */
function requisitionSaveStatusDotClass(saveStatus: SpareRequisition['saveStatus'] | null | undefined): string {
  const st = saveStatus || 'SAVED';
  if (st === 'DRAFT') return 'bg-amber-500 dark:bg-amber-400';
  if (st === 'SAVED') return 'bg-emerald-500 dark:bg-emerald-400';
  return 'bg-slate-400 dark:bg-slate-500';
}

const SaveStatusCell: React.FC<{ row: SpareRequisition }> = ({ row }) => {
  const st = row.saveStatus || 'SAVED';
  const label = st === 'DRAFT' ? 'Draft' : 'Saved';
  const dotClass = requisitionSaveStatusDotClass(row.saveStatus);
  return (
    <div className="max-w-[10rem] py-0.5">
      <StatusDotPill
        dotClassName={dotClass}
        title={st === 'DRAFT' ? 'Draft requisition' : 'Saved requisition'}
      >
        {label}
      </StatusDotPill>
    </div>
  );
};

type PipelineStatusMeta = {
  label: string;
  toneClass: string;
  dotClass: string;
  stepLabel: string;
};

/** On a spare case with any enquiry, quotation, or PO — list/actions treat the row as view-only. */
export function isSpareRequisitionLockedAfterPipelineStep(row: SpareRequisition): boolean {
  const caseId = row.caseId ?? 0;
  if (caseId <= 0) return false;
  const ec = row.pipelineCounts?.enquiryCount ?? 0;
  const qc = row.pipelineCounts?.quotationCount ?? 0;
  const oc = row.pipelineCounts?.orderCount ?? 0;
  return ec > 0 || qc > 0 || oc > 0;
}

/** Permanent delete is blocked until `caseId` is cleared (unlink from spare case). */
export function isSpareRequisitionDeleteBlockedByCaseLink(row: SpareRequisition): boolean {
  return (row.caseId ?? 0) > 0;
}

function getRequisitionPipelineStatus(row: SpareRequisition): PipelineStatusMeta {
  const caseId = row.caseId ?? 0;
  const enquiryCount = row.pipelineCounts?.enquiryCount ?? 0;
  const quotationCount = row.pipelineCounts?.quotationCount ?? 0;
  const orderCount = row.pipelineCounts?.orderCount ?? 0;
  const deliveryCount = row.pipelineCounts?.deliveryCount ?? 0;

  if (caseId <= 0) {
    return {
      label: 'Awaiting office to send for RFQ',
      toneClass: 'border-slate-300/80 bg-slate-100/80 text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200',
      dotClass: 'bg-slate-500 dark:bg-slate-400',
      stepLabel: 'Step 1'};
  }
  if (orderCount > 0 && deliveryCount === 0) {
    return {
      label: 'Pending Delivery to Vessel',
      toneClass: 'border-emerald-300/80 bg-emerald-100/80 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
      dotClass: 'bg-emerald-500 dark:bg-emerald-400',
      stepLabel: 'Step 5'};
  }
  if (orderCount > 0 && deliveryCount > 0) {
    return {
      label: 'Delivered to Vessel',
      toneClass: 'border-teal-300/80 bg-teal-100/80 text-teal-900 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-100',
      dotClass: 'bg-teal-500 dark:bg-teal-400',
      stepLabel: 'Step 6'};
  }
  if (quotationCount > 0 && orderCount === 0) {
    return {
      label: 'Awaiting Office to proceed with Order',
      toneClass: 'border-blue-300/80 bg-blue-100/80 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-200',
      dotClass: 'bg-blue-500 dark:bg-blue-400',
      stepLabel: 'Step 4'};
  }
  if (enquiryCount > 0 && quotationCount === 0 && orderCount === 0) {
    return {
      label: 'Awaiting Supplier to send Quote',
      toneClass: 'border-violet-300/80 bg-violet-100/80 text-violet-800 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200',
      dotClass: 'bg-violet-500 dark:bg-violet-400',
      stepLabel: 'Step 3'};
  }
  return {
    label: 'Awaiting Office to send RFQ',
    toneClass: 'border-amber-300/80 bg-amber-100/80 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
    dotClass: 'bg-amber-500 dark:bg-amber-400',
    stepLabel: 'Step 2'};
}

export const PipelineStatusCell: React.FC<{ row: SpareRequisition }> = ({ row }) => {
  const status = getRequisitionPipelineStatus(row);
  return (
    <div className="max-w-[19rem] py-0.5" title={status.label}>
      <StatusDotPill
        className={status.toneClass}
        dotClassName={status.dotClass}
        title={status.label}
      >
        <span className="text-[0.68rem] uppercase opacity-85">{status.stepLabel}</span>
        {' '}
        {status.label}
      </StatusDotPill>
    </div>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SpareRequisition>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.vessel?.vesselName} />},
  { key: 'reqDate', header: 'Date', sortable: true, render: (v) => formatDate(v) },
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  {
    key: 'saveStatus',
    header: 'Status',
    sortable: true,
    render: (_v, row) => (row ? <SaveStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'pipelineCounts.orderCount',
    header: 'Pipeline Status',
    sortable: false,
    maxWidth: '19rem',
    render: (_v, row) => (row ? <PipelineStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'categories',
    header: 'Categories',
    sortable: false,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <ReqCategoriesCell row={row} /> : <TextCell value="" />)},
  {
    key: 'spareCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <CaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'lines',
    header: 'Lines',
    sortable: true,
    render: (_v, row) => <TextCell value={String(row?._count?.lines ?? row?.lines?.length ?? 0)} />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

function formatRequisitionListActionError(err: unknown, fallback: string): string {
  const data =
    err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
      : undefined;
  const m = data?.message;
  return Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : fallback;
}

const REQ_LIST_ACTION_ERR = {
  editLocked:
    'This requisition cannot be edited while its spare case already has a supplier enquiry, quotation, or purchase order.',
  deleteLocked:
    'This requisition cannot be permanently deleted while a supplier enquiry, quotation, or purchase order is linked to it.',
  deleteRequiresUnlinkFromCase:
    'This requisition is on a spare case. Remove it from the case first, then you can delete it.',
  editNoPermission: 'You do not have permission to edit spare requisitions.',
  deleteNoPermission: 'You do not have permission to delete spare requisitions.',
  copyFailed: 'Could not copy requisition.'} as const;

/** Fixed slots so View / Copy / Edit / Delete stay column-aligned. */
const REQ_ACTION_SLOT = 'flex h-9 w-9 shrink-0 items-center justify-center';

const TableActionsCell: React.FC<{
  item: SpareRequisition;
  onDelete: (id: number) => void;
  pipelineLocked: boolean;
  canEditPermission: boolean;
  canDeletePermission: boolean;
  returnPath?: string | null;
}> = ({ item, onDelete, pipelineLocked, canEditPermission, canDeletePermission, returnPath }) => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [copyBusy, setCopyBusy] = React.useState(false);

  const caseBlocksDelete = isSpareRequisitionDeleteBlockedByCaseLink(item);
  const canEdit = !pipelineLocked && canEditPermission;
  const canDelete = !pipelineLocked && canDeletePermission && !caseBlocksDelete;
  const linkState = returnPath ? { from: returnPath } : undefined;

  const showActionError = (title: string, message: string) => showError(`${title}: ${message}`);

  const onEditBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEditPermission) {
      showActionError('Permission denied', REQ_LIST_ACTION_ERR.editNoPermission);
      return;
    }
    showActionError('Cannot edit', REQ_LIST_ACTION_ERR.editLocked);
  };

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDeletePermission) {
      showActionError('Permission denied', REQ_LIST_ACTION_ERR.deleteNoPermission);
      return;
    }
    if (pipelineLocked) {
      showActionError('Cannot delete', REQ_LIST_ACTION_ERR.deleteLocked);
      return;
    }
    showActionError('Cannot delete', REQ_LIST_ACTION_ERR.deleteRequiresUnlinkFromCase);
  };

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setCopyBusy(true);
    try {
      const created = (await duplicateSpareRequisition(item.id)) as SpareRequisition;
      if (created?.id) {
        navigate(moduleConfig.module.route.edit(created.id), {
          state: linkState});
      }
    } catch (err: unknown) {
      showActionError('Could not copy', formatRequisitionListActionError(err, REQ_LIST_ACTION_ERR.copyFailed));
    } finally {
      setCopyBusy(false);
    }
  };

  const editClass =
    'p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50';

  return (
    <>
      <div className="flex items-center flex-nowrap gap-0">
        <div className={REQ_ACTION_SLOT}>
          <Link
            to={moduleConfig.module.route.detail(item.id)}
            state={linkState}
            className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            title="View"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </Link>
        </div>
        <div className={REQ_ACTION_SLOT}>
          <button
            type="button"
            disabled={copyBusy}
            className="p-1 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
            onClick={(e) => void onCopy(e)}
            title="Copy as new draft"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>
        <div className={REQ_ACTION_SLOT}>
          {canEdit ? (
            <Link
              to={moduleConfig.module.route.edit(item.id)}
              state={linkState}
              className={editClass}
              onClick={(e) => e.stopPropagation()}
              title="Edit"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Link>
          ) : (
            <button type="button" className={editClass} onClick={onEditBlocked} title="Edit">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
        </div>
        <div className={REQ_ACTION_SLOT}>
          {canDelete ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item.id);
              }}
              className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete requisition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
              onClick={onDeleteBlocked}
              title="Delete requisition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export const getTableActions = (
  item: SpareRequisition,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean; returnPath?: string | null },
): React.ReactNode => {
  const locked = isSpareRequisitionLockedAfterPipelineStep(item);
  return (
    <TableActionsCell
      item={item}
      onDelete={onDelete}
      pipelineLocked={locked}
      canEditPermission={options?.canEdit !== false}
      canDeletePermission={options?.canDelete !== false}
      returnPath={options?.returnPath}
    />
  );
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
